import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Activity,
  AlertTriangle,
  Bug,
  Eye,
  EyeOff,
  ChevronDown,
  GitBranch,
  MessageSquare,
  RotateCcw,
  Search,
  UserX,
  X
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useChatContext } from '../../contexts/AppDataContext';
import { useDowntime } from '../../contexts/DowntimeContext';
import { ErrorState } from './ErrorState';
import '../../Styles/Layout/DevToolBar.css';
import { ConvexMessage } from '../../types/message';

type DevPanel = 'state' | 'branches' | 'messages' | 'errors';
type MessageFilter = 'all' | 'ai' | 'user';

const formatTimestamp = (timestamp: number | string | undefined): string => {
  if (!timestamp) return 'No timestamp';

  try {
    return new Date(timestamp).toLocaleString();
  } catch (_error) {
    return String(timestamp);
  }
};

const truncateContent = (content: string | undefined, maxLength = 120): string => {
  if (!content) return 'No content';
  return content.length > maxLength ? `${content.slice(0, maxLength)}...` : content;
};

export default function DevToolbar(): React.ReactElement | null {
  const auth = useAuth();
  const {
    downtime,
    serverDowntime,
    showDowntimePreview,
    setShowDowntimePreview,
    version
  } = useDowntime();
  const {
    messages,
    filteredMessages,
    globalBranchCount,
    setGlobalBranchCount,
    maxBranchId
  } = useChatContext();

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [activePanel, setActivePanel] = useState<DevPanel>('state');
  const [showDowntimeDetails, setShowDowntimeDetails] = useState<boolean>(false);
  const [manualBranchCount, setManualBranchCount] = useState<number>(globalBranchCount || 1);
  const [messageFilter, setMessageFilter] = useState<MessageFilter>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [position, setPosition] = useState<{ x: number; y: number } | null>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const dragStartPos = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  const hasServerDowntime = serverDowntime.isDowntime;

  useEffect(() => {
    if (globalBranchCount) {
      setManualBranchCount(globalBranchCount);
    }
  }, [globalBranchCount]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        return;
      }

      const target = event.target;
      const isTypingTarget = target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement;
      const isSecretCombo = event.altKey && event.shiftKey && event.key.toLowerCase() === 'd';
      const isLegacyCombo = event.shiftKey && event.key.toLowerCase() === 'd' && !isTypingTarget;

      if (isSecretCombo || isLegacyCombo) {
        event.preventDefault();
        setIsOpen((current) => !current);
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, []);

  useEffect(() => {
    const handleMouseMove = (event: MouseEvent): void => {
      if (!isDragging) return;

      setPosition({
        x: event.clientX - dragStartPos.current.x,
        y: event.clientY - dragStartPos.current.y
      });
    };

    const handleMouseUp = (): void => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  const filteredMessageList = useMemo(() => {
    const source = messages || [];
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return source.filter((message) => {
      if (messageFilter === 'ai' && !message.isAi) return false;
      if (messageFilter === 'user' && message.isAi) return false;

      if (normalizedSearch) {
        return (message.content || '').toLowerCase().includes(normalizedSearch);
      }

      return true;
    });
  }, [messageFilter, messages, searchTerm]);

  if (process.env.NODE_ENV !== 'development') {
    return null;
  }

  const handleResetChats = (): void => {
    auth.reduceUsageData(9999);
  };

  const updateGlobalBranchCount = (): void => {
    setGlobalBranchCount(manualBranchCount);
  };

  const setBranchToMax = (): void => {
    setGlobalBranchCount(maxBranchId);
    setManualBranchCount(maxBranchId);
  };

  const onDragStart = (event: React.MouseEvent<HTMLDivElement>): void => {
    if (!toolbarRef.current) return;

    const rect = toolbarRef.current.getBoundingClientRect();
    dragStartPos.current = {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top
    };
    setIsDragging(true);
  };

  const toolbarStyle: React.CSSProperties = position
    ? {
        left: `${position.x}px`,
        top: `${position.y}px`,
        right: 'auto',
        bottom: 'auto'
      }
    : {};

  if (!isOpen) return null;

  return (
    <section
      className="dev-toolbar"
      ref={toolbarRef}
      style={toolbarStyle}
      aria-label="Dev toolbar"
    >
      <header className="dev-toolbar-header" onMouseDown={onDragStart}>
        <div className="dev-toolbar-heading">
          <Bug size={16} />
          <div>
            <h3>Dev Toolbar</h3>
            <p>Development controls</p>
          </div>
        </div>
        <button
          type="button"
          className="dev-icon-button"
          onClick={() => setIsOpen(false)}
          aria-label="Close dev toolbar"
          title="Close"
        >
          <X size={16} />
        </button>
      </header>

      <nav className="dev-toolbar-tabs" aria-label="Dev toolbar panels">
        <button
          type="button"
          className={activePanel === 'state' ? 'active' : ''}
          onClick={() => setActivePanel('state')}
        >
          <Activity size={15} />
          <span>State</span>
        </button>
        <button
          type="button"
          className={activePanel === 'branches' ? 'active' : ''}
          onClick={() => setActivePanel('branches')}
        >
          <GitBranch size={15} />
          <span>Branches</span>
        </button>
        <button
          type="button"
          className={activePanel === 'messages' ? 'active' : ''}
          onClick={() => setActivePanel('messages')}
        >
          <MessageSquare size={15} />
          <span>Messages</span>
        </button>
        <button
          type="button"
          className={activePanel === 'errors' ? 'active' : ''}
          onClick={() => setActivePanel('errors')}
        >
          <AlertTriangle size={15} />
          <span>Errors</span>
        </button>
      </nav>

      {activePanel === 'state' && (
        <div className="dev-panel">
          <div className="dev-state-grid">
            <div className="dev-stat">
              <span>Latest</span>
              <strong>{version.latest}</strong>
            </div>
            <div className="dev-stat">
              <span>Minimum</span>
              <strong>{version.minimum}</strong>
            </div>
            <div className="dev-stat">
              <span>Server Downtime</span>
              <strong className={hasServerDowntime ? 'danger' : 'ok'}>
                {hasServerDowntime ? 'Active' : 'Inactive'}
              </strong>
            </div>
            <div className="dev-stat">
              <span>Preview</span>
              <strong>{showDowntimePreview ? 'On' : 'Off'}</strong>
            </div>
          </div>

          <div className="dev-toolbar-actions">
            <button
              type="button"
              className={`dev-action-button ${showDowntimePreview ? 'active' : ''}`}
              onClick={() => setShowDowntimePreview((current) => !current)}
              disabled={!hasServerDowntime}
            >
              {showDowntimePreview ? <EyeOff size={15} /> : <Eye size={15} />}
              <span>{showDowntimePreview ? 'Hide Downtime' : 'Preview Downtime'}</span>
            </button>
            <button type="button" className="dev-action-button" onClick={handleResetChats}>
              <RotateCcw size={15} />
              <span>Reset Chats</span>
            </button>
            <button type="button" className="dev-action-button" disabled>
              <UserX size={15} />
              <span>Clear User</span>
            </button>
          </div>

          <section className={`dev-downtime-row ${showDowntimeDetails ? 'open' : ''}`}>
            <button
              type="button"
              className="dev-downtime-row-trigger"
              onClick={() => setShowDowntimeDetails((current) => !current)}
              aria-expanded={showDowntimeDetails}
            >
              <span>
                Downtime
                <strong className={hasServerDowntime ? 'danger' : 'ok'}>
                  {hasServerDowntime ? 'Active' : 'Inactive'}
                </strong>
              </span>
              <ChevronDown size={15} />
            </button>

            {showDowntimeDetails && (
              <div className="dev-downtime-details">
                <p>{serverDowntime.message || 'No downtime message from the API.'}</p>
                <dl>
                  <div>
                    <dt>Reason</dt>
                    <dd>{serverDowntime.reason || 'None'}</dd>
                  </div>
                  <div>
                    <dt>Resolution</dt>
                    <dd>{serverDowntime.resolution || 'Not set'}</dd>
                  </div>
                  <div>
                    <dt>Effective</dt>
                    <dd>{downtime.isDowntime ? 'Showing downtime' : 'App available'}</dd>
                  </div>
                </dl>
              </div>
            )}
          </section>
        </div>
      )}

      {activePanel === 'branches' && (
        <div className="dev-panel">
          <div className="dev-state-grid">
            <div className="dev-stat">
              <span>Messages</span>
              <strong>{messages?.length || 0}</strong>
            </div>
            <div className="dev-stat">
              <span>Filtered</span>
              <strong>{filteredMessages?.length || 0}</strong>
            </div>
            <div className="dev-stat">
              <span>Current</span>
              <strong>{globalBranchCount}</strong>
            </div>
            <div className="dev-stat">
              <span>Max</span>
              <strong>{maxBranchId}</strong>
            </div>
          </div>

          <div className="dev-branch-controls">
            <input
              type="number"
              min="1"
              value={manualBranchCount}
              onChange={(event) => setManualBranchCount(Number(event.target.value))}
              aria-label="Branch count"
            />
            <button type="button" className="dev-action-button" onClick={updateGlobalBranchCount}>
              <GitBranch size={15} />
              <span>Update</span>
            </button>
            <button type="button" className="dev-action-button" onClick={setBranchToMax}>
              <span>Set Max</span>
            </button>
          </div>
        </div>
      )}

      {activePanel === 'messages' && (
        <div className="dev-panel">
          <div className="dev-message-toolbar">
            <label className="dev-search-field">
              <Search size={15} />
              <input
                type="text"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search messages"
              />
            </label>

            <div className="dev-segmented-control">
              {(['all', 'ai', 'user'] as MessageFilter[]).map((filter) => (
                <button
                  type="button"
                  key={filter}
                  className={messageFilter === filter ? 'active' : ''}
                  onClick={() => setMessageFilter(filter)}
                >
                  {filter.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          <div className="dev-message-summary">
            <span>Total {messages?.length || 0}</span>
            <span>Showing {filteredMessageList.length}</span>
          </div>

          <div className="dev-message-list">
            {filteredMessageList.length > 0 ? (
              filteredMessageList.map((message: ConvexMessage) => (
                <article
                  key={message._id}
                  className={`dev-message-item ${message.isAi ? 'ai' : 'user'}`}
                >
                  <div className="dev-message-header">
                    <span>{message.isAi ? 'AI' : 'User'}</span>
                    <code>
                      {typeof message._id === 'string'
                        ? `${message._id.slice(0, 8)}...`
                        : String(message._id)}
                    </code>
                    {message.timestamp && <time>{formatTimestamp(message.timestamp)}</time>}
                  </div>
                  <p>{truncateContent(message.content)}</p>
                  <div className="dev-message-tags">
                    {message.model && <span>{message.model}</span>}
                    {message.branchId !== undefined && <span>Branch {message.branchId}</span>}
                    {message.mode && <span>{message.mode.name}</span>}
                    {message.isInterrupted && <span>Interrupted</span>}
                    {message.isStopped && <span>Stopped</span>}
                  </div>
                </article>
              ))
            ) : (
              <p className="dev-empty-state">No messages to display</p>
            )}
          </div>
        </div>
      )}

      {activePanel === 'errors' && (
        <div className="dev-panel">
          <div className="dev-error-preview-list">
            <ErrorState
              compact
              eyebrow="Application error"
              title="StudyBuddy hit an unexpected problem"
              message="Refresh the app to start cleanly, or return home if this happened while opening a specific page."
              primaryAction={{ label: 'Refresh app', onClick: () => undefined }}
              secondaryAction={{ label: 'Return home', onClick: () => undefined }}
              error={new Error('Preview root error')}
            />
            <ErrorState
              compact
              eyebrow="Data connection"
              title="Messages could not load"
              message="The realtime data layer had trouble rendering this view. Try again after a moment."
              primaryAction={{ label: 'Try again', onClick: () => undefined }}
              error={new Error('Preview data error')}
            />
            <ErrorState
              compact
              eyebrow="Chat view"
              title="This chat view stopped rendering"
              message="Your conversation is not deleted. Try reopening the view, or refresh if the issue keeps happening."
              primaryAction={{ label: 'Try again', onClick: () => undefined }}
              secondaryAction={{ label: 'Refresh page', onClick: () => undefined }}
              error={new Error('Preview chat error')}
            />
            <ErrorState
              compact
              eyebrow="Chat history"
              title="Unable to load chats"
              message="Your conversations are still safe. Try reloading the list."
              primaryAction={{ label: 'Try again', onClick: () => undefined }}
              supportHref=""
              error={new Error('Preview sidebar error')}
            />
          </div>
        </div>
      )}
    </section>
  );
}
